<template>
  <div v-if="success" class="wrapper-success-response">
    <p
      >{{ $t(`${base}.success.description`) }}
      <nuxt-link v-if="code" :to="`/user/reservation/${code}/chat`">{{
        $t(`${base}.success.here`)
      }}</nuxt-link></p
    >
  </div>
  <el-form
    v-else
    ref="questionForm"
    v-loading="loading"
    :model="form"
    :rules="rules"
    :status-icon="true"
    auto-complete="on"
    label-position="top"
    class="lad-question--body"
    @submit.native.prevent
  >
    <div class="input-group">
      <el-form-item
        :label="$t(`${baseForm}.labels.profile.firstname`)"
        prop="profile.firstname"
      >
        <el-input
          v-model="form.profile.firstname"
          :disabled="isAuth"
          type="text"
          name="firstname"
          auto-complete="given-name"
        />
      </el-form-item>

      <el-form-item
        :label="$t(`${baseForm}.labels.profile.lastname`)"
        prop="profile.lastname"
      >
        <el-input
          v-model="form.profile.lastname"
          :disabled="isAuth"
          type="text"
          name="lastname"
          auto-complete="family-name"
        />
      </el-form-item>
    </div>

    <el-form-item :label="$t(`${baseForm}.labels.email`)" prop="email">
      <el-input
        v-model="form.email"
        :disabled="isAuth"
        type="email"
        name="email"
        auto-complete="email"
      />
    </el-form-item>

    <el-form-item
      :label="$t(`${baseForm}.labels.profile.phone.number`)"
      prop="profile.phone.number"
    >
      <LadInputPhone
        :dial="form.profile.phone.dial"
        :number="form.profile.phone.number"
        :disabled="isAuth"
        @change="handleChangePhone"
      />
    </el-form-item>

    <div class="input-group">
      <el-form-item :label="$t(`${baseForm}.labels.qty`)" prop="qty">
        <el-input-number
          v-model="form.qty"
          :min="currentDepartureDate.min_travelers"
          :max="currentDepartureDate.max_travelers"
        />
      </el-form-item>

      <el-form-item :label="$t(`${baseForm}.labels.date`)" prop="date">
        <el-date-picker
          v-model="form.date"
          type="date"
          format="yyyy/MM/dd"
          value-format="yyyy-MM-dd"
          :picker-options="dateOptions"
          :placeholder="$t(`${baseForm}.labels.date`)"
          tabindex="1"
        />
      </el-form-item>
    </div>

    <el-form-item :label="$t(`${baseForm}.labels.question`)" prop="question">
      <el-input
        v-model="form.question"
        type="textarea"
        :placeholder="$t(`${baseForm}.labels.question`)"
      ></el-input>
    </el-form-item>

    <el-alert
      v-if="userExist && !isAuth"
      :title="$t(`checkout.general_info.before_continuing`)"
      type="warning"
      show-icon
      :closable="false"
    >
      <div class="wrapper-error-alert">
        <div>
          <p v-html="$t(`checkout.general_info.exist_user`)"></p>
        </div>

        <div>
          <el-button @click="toggleAuthModal">{{
            $t('auth.btn_login')
          }}</el-button>
        </div>
      </div>
    </el-alert>

    <el-button
      type="success"
      :loading="loading"
      class="submit-button"
      @click="handleValidate"
      >{{ $t(`${baseForm}.labels.cta`) }}</el-button
    >
  </el-form>
</template>

<script>
import { mapState } from 'vuex'
import Cookies from 'js-cookie'
import { validateEmail } from '@/utils/validate'
import FormValidationMixin from '@/utils/mixin/form-validation'
import DetectAutFillMixin from '@/utils/mixin/detect-autofill'
import { hasOwnProperties } from '@/utils'

export default {
  name: 'LadAskQuestionContent',
  components: {
    LadInputPhone: () =>
      import(
        /* webpackChunkName: "lad-input-phone" */ '@/components/frontends/InputPhone/index'
      ),
  },
  mixins: [FormValidationMixin, DetectAutFillMixin],
  props: {
    success: { type: Boolean, default: false },
    code: { type: String, default: null },
    handleSuccess: { type: Function, default: () => {} },
  },
  data() {
    const base = `components.ask_question`
    const baseForm = `${base}.form`

    /** validating required field */
    const validateRequiredField = (rule, value, callback) => {
      let error
      const field = rule.field

      if (!value || value.length === 0) {
        const name = this.$t(`${baseForm}.labels.${field}`)

        error = new Error(
          this.$t(`general.validations.required_input`, { name })
        )
      }

      callback(error)
    }

    /** validating email field */
    const validateEmailField = (rule, value, callback) => {
      let error
      const field = rule.field

      if (!value) {
        const name = this.$t(`${baseForm}.labels.${field}`)

        error = new Error(
          this.$t(`general.validations.required_input`, { name })
        )
      } else if (!validateEmail(value)) {
        error = new Error(this.$t('general.validations.invalid_email'))
      }

      callback(error)
    }

    /** adiciona mensajes de validacion del servidor */
    const serverValidation = (rule, value, callback) => {
      let error
      const field = rule.field

      if (Reflect.has(this.serverErrors, field)) {
        error = new Error(this.serverErrors[field][0].replace(field, ''))
      }

      callback(error)
    }

    /** define la disponibilidad de las fechas */
    const disabledDate = (time) => {
      const date = this.$dayjs(time).format('YYYY-MM-DD')

      return !this.availableDates.includes(date)
    }

    return {
      base,
      baseForm,
      loading: false,
      dateOptions: {
        disabledDate,
      },
      userExist: false,
      form: {
        email: '',
        currency: '',
        locale: '',
        invitation: false,
        profile: {
          firstname: '',
          lastname: '',
          phone: {
            dial: '',
            number: '',
          },
        },
        qty: 1,
        date: null,
        question: '',
        referral: '',
      },
      rules: {
        email: [
          {
            required: true,
            type: 'email',
            trigger: 'blur',
            validator: validateEmailField,
          },
          { trigger: 'blur', validator: serverValidation },
        ],
        profile: {
          firstname: [
            {
              required: true,
              trigger: 'blur',
              validator: validateRequiredField,
            },
            { trigger: 'blur', validator: serverValidation },
          ],
          lastname: [
            {
              required: true,
              trigger: 'blur',
              validator: validateRequiredField,
            },
            { trigger: 'blur', validator: serverValidation },
          ],
          phone: {
            number: [
              {
                required: true,
                trigger: 'blur',
                validator: validateRequiredField,
              },
              {
                required: true,
                trigger: 'blur',
                message: this.$t(`general.validations.min_max`, {
                  min: 4,
                  max: 15,
                }),
              },
              { trigger: 'blur', validator: serverValidation },
            ],
          },
        },
        qty: [
          {
            required: true,
            trigger: 'blur',
            validator: validateRequiredField,
          },
          { trigger: 'blur', validator: serverValidation },
        ],
        date: [
          {
            required: true,
            trigger: 'blur',
            validator: validateRequiredField,
          },
          { trigger: 'blur', validator: serverValidation },
        ],
        question: [
          {
            required: true,
            trigger: 'blur',
            validator: validateRequiredField,
          },
          { trigger: 'blur', validator: serverValidation },
        ],
      },
      serverErrors: {},
    }
  },
  computed: {
    ...mapState('settings', ['locale', 'currency']),
    ...mapState('modals', {
      isAskQuestionModalOpen: (state) => state.askQuestion.opened,
    }),
    ...mapState('availability', {
      availability: (state) => {
        const list = []
        state.availability.ranges.forEach((range) => {
          range.items.forEach((t) => {
            t.availability.forEach((a) => list.push(a))
          })
        })

        return list
      },
      availableDates: (state) => {
        const list = []
        state.availability.ranges.forEach((range) => {
          range.items.forEach((t) => {
            t.availability.forEach((a) => list.push(a.datetime.start))
          })
        })

        return list
      },
    }),
    ...mapState('auth', {
      firstname: (state) => (state.user ? state.user.profile.firstname : null),
      lastname: (state) => (state.user ? state.user.profile.lastname : null),
      phone: (state) => (state.user ? state.user.profile.phone : null),
      email: (state) => (state.user ? state.user.email : null),
      userUuid: (state) => (state.user ? state.user.uuid : null),
    }),
    ...mapState('experience', {
      promotions: 'promotions',
      exExperience: (state) => ({
        uuid: state.experience.uuid,
        name: state.experience.name,
      }),
    }),
    currentDepartureDate() {
      const items = this.availability.filter(
        (t) => t.datetime.start === this.form.date
      )

      if (items.length > 0) {
        return {
          min_travelers: Math.min(...items.map((i) => i.min_travelers)) || 1,
          max_travelers: Math.max(...items.map((i) => i.max_travelers)) || 100,
        }
      }
      return {
        min_travelers: 1,
        max_travelers: 100,
      }
    },
  },
  watch: {
    'form.profile.firstname'(val) {
      this.form.profile.firstname = val.trim()
    },
    'form.profile.lastname'(val) {
      this.form.profile.lastname = val.trim()
    },
    isAskQuestionModalOpen: {
      immediate: true,
      handler(newValue) {
        if (!newValue && this.$refs.questionForm) {
          this.$refs.questionForm.resetFields()
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        const isFirstLoad = typeof oldValue === 'undefined'

        if (isFirstLoad) {
          newValue.profile.firstname = this.firstname || ''
          newValue.email = this.email
          newValue.profile.lastname = this.lastname || ''

          newValue.profile.phone.dial =
            this.phone && this.phone.dial ? this.phone.dial : null

          newValue.profile.phone.number =
            this.phone && this.phone.number ? this.phone.number : null
        }
      },
    },
  },
  mounted() {
    this.form.referral = this.$route.params.referral_code || ''
    this.form.currency = this.currency
    this.form.locale = this.locale
  },
  methods: {
    handleChangePhone({ dial, number }) {
      this.form.profile.phone.dial = dial
      this.form.profile.phone.number = number
    },
    handleChangeQty(qty) {
      this.form.qty = qty
    },
    toggleAuthModal() {
      this.$store.dispatch('modals/toggle', 'auth')
    },
    handleValidate() {
      this.serverErrors = {}

      this.$refs.questionForm.validate((valid) => {
        if (valid) {
          this.handleSubmit()
        } else if (
          typeof this.$bugsnag === 'object' &&
          typeof this.$bugsnag.notify === 'function'
        ) {
          this.$bugsnag.notify(
            new Error(`invalid form on ask question`),
            (event) => {
              event.addMetadata('form', this.form)
            }
          )
        }
      })
    },
    async handleSubmit() {
      this.loading = true

      try {
        if (!this.isAuth) {
          if (!this.form.referral) {
            this.form.referral = Cookies.get('lad-referrals') || ''
          }

          const formData = {
            email: this.form.email,
            profile: {
              firstname: this.form.profile.firstname,
              lastname: this.form.profile.lastname,
              phone: {
                dial: this.form.profile.phone.dial,
                number: this.form.profile.phone.number,
              },
            },
            currency: this.form.currency,
            locale: this.form.locale,
            referral: this.form.referral,
          }

          const { data } = await this.$axios.post(
            '/api/auth/register',
            formData
          )

          if (hasOwnProperties(data, 'user_exist') && data.user_exist) {
            this.userExist = true
            this.loading = false
          } else {
            await this.$auth
              .loginWith('custom-login', {
                data: {
                  username: this.form.email,
                  password: this.form.email,
                },
              })
              .then(() => {
                if (typeof this.$zohoWidget !== 'undefined') {
                  this.$zohoWidget.visitor(this.form.email)
                }
              })
              .then(async (response) => {
                await this.$auth
                  .setUserToken(response.access_token, response.refresh_token)
                  .finally(async () => await this.createOrder())
              })
              .finally(() => {})
          }
        } else {
          await this.createOrder()
        }
      } catch (error) {
        if (hasOwnProperties(error, 'response.data')) {
          const data = error.response.data

          if (hasOwnProperties(data, 'errors')) {
            this.serverErrors = data.errors
          } else if (hasOwnProperties(data, 'message')) {
            this.serverErrors.email = [data.message]
          }

          if (typeof this.$refs.questionForm !== 'undefined') {
            this.$refs.questionForm.validate(() => {})
          }
        }

        if (
          typeof this.$bugsnag === 'object' &&
          typeof this.$bugsnag.notify === 'function'
        ) {
          this.$bugsnag.notify(error)
        }
      }

      this.loading = false
    },
    async createOrder() {
      const availability = this.availability.find((a) => {
        let result = a.datetime.start === this.form.date
        if (a.datetime.min_travelers) {
          result = result && a.datetime.min_travelers <= this.form.qty
        }
        if (a.datetime.max_travelers) {
          result = result && a.datetime.max_travelers >= this.form.qty
        }
        return result
      })

      const data = {
        extras: [],
        insurances: [],
        rooms: [],
        flights: [],
        experiences: [
          {
            uuid: this.exExperience.uuid,
            price: availability.price,
            qty: this.form.qty,
            unit: 1,
            open: false,
            date: this.form.date,
            departure_date_code: availability.code,
            season: availability.season,
            language: availability.language,
            parent: { uuid: null, index: null },
            promotions: this.promotions.map((t) => ({ uuid: t.uuid })),
            coupons: [],
          },
        ],
        packages: [],
        payment: {
          recurring: {
            active: false,
            interval: { type: 'monthly', quantity: 0 },
          },
          pay_in_full: false,
          method: 'whatsapp',
        },
        device_id: this.deviceId,
        creation_section: 'client_dialog_ask_question_reservation_creation',
      }

      try {
        const { status, chat, order } = await this.$axios.post(
          `/api/v1.5/orders/create`,
          data
        )

        if (status === 'success' && chat.uuid) {
          await this.$axios.post(`/api/chat/${chat.uuid}`, {
            attachment: { name: '', type: '', extension: 'none', base64: '' },
            message: this.form.question,
            type: 'text/plain',
            group: { uuid: chat.uuid },
          })
          this.handleSuccess(order.code)
        }
      } catch (error) {}
    },
  },
}
</script>

<style lang="scss">
.lad-question--body {
  .input-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .form-group-referral {
    .el-form-item__label {
      font-weight: normal;
      line-height: 1.8;
      font-size: 0.9em;
    }
  }

  .submit-button {
    text-transform: uppercase;
    width: 100%;
  }

  a[href] {
    text-decoration: underline;
  }

  .el-input,
  .el-input-number {
    width: 100% !important;
  }

  .el-alert--warning {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  @media screen and (max-width: 420px) {
    .input-group {
      grid-template-columns: 1fr;
    }
  }
}

.wrapper-success-response {
  text-align: center;

  p {
    font-size: 18px;

    a {
      font-weight: bold;
    }
  }
}
</style>
